<template>
  <div class="step-player">
    <div class="stage" ref="stage" :style="stageStyle">
      <div class="canvas" :style="canvasStyle">
        <img v-if="resolvedImage" class="base-image" :src="resolvedImage" draggable="false" />
        <img v-if="resolvedOverlayImage" class="overlay-image" :src="resolvedOverlayImage" :style="overlayStyle" draggable="false" />

        <template v-for="label in resolvedLabels">
          <div
            :key="label.id"
            class="label"
            :style="[rectStyle(label), labelStyle(label)]"
          >
            {{ label.text }}
          </div>
        </template>

        <template v-for="img in resolvedExtraImages">
          <img
            :key="img.id"
            class="extra-image"
            :src="img.src"
            :style="[rectStyle(img), extraImageStyle(img)]"
            draggable="false"
          />
        </template>

        <template v-for="hotspot in resolvedHotspots">
          <div
            :key="hotspot.id"
            class="hotspot"
            :style="[rectStyle(hotspot), hotspotStyle(hotspot)]"
            @mouseenter="onEnter(hotspot)"
            @mouseleave="onLeave(hotspot)"
            @click="onClick(hotspot)"
            @contextmenu.prevent="onContextMenu(hotspot)"
            @dblclick.prevent="onDblClick(hotspot)"
          />
        </template>

      <template v-for="field in resolvedFields">
        <div
          :key="field.id"
          class="field"
          :style="[rectStyle(field), fieldStyle(field)]"
          :data-field-id="field.id"
        >
            <div
              v-if="field.type === 'label'"
              class="field-label"
              :style="fieldLabelStyle(field)"
            >
              {{ form[field.model] }}
            </div>
            <input
              v-else-if="field.type !== 'textarea' && field.type !== 'select'"
              :type="field.type || 'text'"
              class="field-input"
              :placeholder="field.placeholder"
              :value="form[field.model]"
              :autocomplete="field.autocomplete || 'off'"
            :style="fieldInputStyle(field)"
            :data-field-id="field.id"
              @input="onFieldInput(field, $event)"
              @keyup.enter="onEnterSubmit(field)"
            />
            <select
              v-else-if="field.type === 'select'"
              class="field-input field-select"
              :value="form[field.model]"
              :style="fieldInputStyle(field)"
              :data-field-id="field.id"
              @change="onFieldInput(field, $event)"
            >
              <option value="">{{ field.placeholder || '请选择' }}</option>
              <option v-for="option in field.options || []" :key="option.value || option" :value="option.value || option">
                {{ option.label || option }}
              </option>
            </select>
            <textarea
              v-else
              class="field-input field-textarea"
              :placeholder="field.placeholder"
              :value="form[field.model]"
            :style="fieldInputStyle(field)"
            :data-field-id="field.id"
              @input="onFieldInput(field, $event)"
            />
          </div>
        </template>

        <template v-for="table in resolvedTables">
          <SceneTable
            :key="table.id"
            :config="table"
            :style="rectStyle(table)"
            @selection-change="onTableSelectionChange(table, $event)"
            @cell-change="onTableCellChange(table, $event)"
            @page-change="onTablePageChange(table, $event)"
          />
        </template>
      </div>
    </div>

    <div v-if="showTip" class="tip">
      <slot name="tip">
        <div class="tip-title">提示</div>
        <div class="tip-body" v-html="resolvedTip"></div>
      </slot>
    </div>
  </div>
</template>

<script>
import SceneTable from './SceneTable.vue'

export default {
  name: 'StepPlayer',
  components: { SceneTable },
  props: {
    steps: { type: Array, required: true },
    initialStepId: { type: String, required: true },
    showTip: { type: Boolean, default: true },
    initialForm: { type: Object, default: () => ({}) },
    initialUi: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      currentStepId: this.initialStepId,
      hoveredHotspotId: '',
      ui: { ...this.initialUi },
      form: { ...this.initialForm },
      _afterEnterTimer: null
    }
  },
  computed: {
    stepMap() {
      const map = Object.create(null)
      ;(this.steps || []).forEach(s => {
        if (s && s.id) map[s.id] = s
      })
      return map
    },
    currentStep() {
      return this.stepMap[this.currentStepId] || { id: this.currentStepId }
    },
    resolvedHotspots() {
      const hs = this.currentStep && this.currentStep.hotspots
      const raw = typeof hs === 'function' ? hs({ ui: this.ui, form: this.form }) : hs
      return Array.isArray(raw) ? raw : []
    },
    resolvedFields() {
      const fs = this.currentStep && this.currentStep.fields
      const raw = typeof fs === 'function' ? fs({ ui: this.ui, form: this.form }) : fs
      return Array.isArray(raw) ? raw : []
    },
    resolvedLabels() {
      const ls = this.currentStep && this.currentStep.labels
      const raw = typeof ls === 'function' ? ls({ ui: this.ui, form: this.form }) : ls
      return Array.isArray(raw) ? raw : []
    },
    resolvedExtraImages() {
      const imgs = this.currentStep && this.currentStep.extraImages
      const raw = typeof imgs === 'function' ? imgs({ ui: this.ui, form: this.form }) : imgs
      const list = Array.isArray(raw) ? raw : []
      return list
        .filter(i => i && i.id && i.src)
        .map(i => ({ ...i, src: this.normalizeSrc(typeof i.src === 'function' ? i.src({ ui: this.ui, form: this.form }) : i.src) }))
    },
    resolvedTables() {
      const ts = this.currentStep && this.currentStep.tables
      const raw = typeof ts === 'function' ? ts({ ui: this.ui, form: this.form }) : ts
      if (!Array.isArray(raw)) return []
      return raw.map(table => {
        const selectedKeys = this.ui[table.id + 'SelectedKeys']
        return Array.isArray(selectedKeys) ? { ...table, selectedKeys } : table
      })
    },
    resolvedOverlayImage() {
      const img = this.currentStep && this.currentStep.overlayImage
      const raw = typeof img === 'function' ? img({ ui: this.ui, form: this.form }) : img
      return this.normalizeSrc(raw)
    },
    resolvedImage() {
      const img = this.currentStep.image
      const raw = typeof img === 'function' ? img({ ui: this.ui, form: this.form }) : img
      return this.normalizeSrc(raw)
    },
    resolvedTip() {
      const tip = this.currentStep.tip
      if (typeof tip === 'function') return tip({ ui: this.ui, form: this.form })
      return tip || ''
    },
    stageStyle() {
      const bg = this.currentStep && this.currentStep.stageBg
      const raw = typeof bg === 'function' ? bg({ ui: this.ui, form: this.form }) : bg
      const src = this.normalizeSrc(raw)
      const ratioRaw = this.currentStep && this.currentStep.aspectRatio
      const ratio = typeof ratioRaw === 'number' && ratioRaw > 0 ? ratioRaw : 1.766
      const style = {
        '--stage-ratio': ratio
      }
      if (!src) return style
      return {
        ...style,
        backgroundImage: `url(${src})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }
    },
    canvasStyle() {
      const scaleRaw = this.currentStep && this.currentStep.canvasScale
      const scale = typeof scaleRaw === 'number' && scaleRaw > 0 && scaleRaw <= 1 ? scaleRaw : 1
      const pad = (1 - scale) / 2
      return {
        left: pad * 100 + '%',
        top: pad * 100 + '%',
        width: scale * 100 + '%',
        height: scale * 100 + '%'
      }
    },
    overlayStyle() {
      const scaleRaw = this.currentStep && this.currentStep.overlayScale
      const raw = typeof scaleRaw === 'function' ? scaleRaw({ ui: this.ui, form: this.form }) : scaleRaw
      const scale = typeof raw === 'number' && raw > 0 && raw <= 1 ? raw : 1
      return {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: scale * 100 + '%',
        height: scale * 100 + '%'
      }
    }
  },
  watch: {
    initialStepId: {
      immediate: false,
      handler(next) {
        this.reset(next)
      }
    },
    initialForm: {
      deep: true,
      handler(next) {
        this.form = { ...(next || {}) }
      }
    },
    initialUi: {
      deep: true,
      handler(next) {
        this.ui = { ...(next || {}) }
      }
    },
    currentStepId(next) {
      if (this._afterEnterTimer) {
        clearTimeout(this._afterEnterTimer)
        this._afterEnterTimer = null
      }
      this.$emit('step-change', next, this.currentStep, this.resolvedTip)
      if (this.currentStep && this.currentStep.onEnter) {
        this.currentStep.onEnter({ go: this.go, ui: this.ui, form: this.form, emit: this.$emit.bind(this) })
      }
      if (this.currentStep && this.currentStep.afterEnter) {
        const { delay, go: goTo, set, setForm, finish } = this.currentStep.afterEnter
        if (set) this.applySet(set)
        if (setForm) this.applyFormSet(setForm)
        if (delay != null && (goTo || finish)) {
          this._afterEnterTimer = setTimeout(() => {
            this._afterEnterTimer = null
            if (finish) {
              this.$emit('finish', { stepId: this.currentStepId })
              return
            }
            this.go(goTo)
          }, delay)
        }
      }
      // 步骤切换后，为标记了 autoFocus 的输入框自动聚焦
      this.$nextTick(() => {
        this.focusAutoField()
      })
    }
  },
  mounted() {
    // 初始渲染时也需要把当前步骤提示发给父组件（否则 initialStepId 不变化时不会触发 watcher）
    this.$emit('step-change', this.currentStepId, this.currentStep, this.resolvedTip)
  },
  beforeDestroy() {
    if (this._afterEnterTimer) clearTimeout(this._afterEnterTimer)
  },
  methods: {
    extraImageStyle(img) {
      if (!img) return {}
      if (img.noShadow === true || img.shadow === false) return { boxShadow: 'none' }
      return {}
    },
    labelStyle(label) {
      const selectable = !!(label && label.selectable)
      const style = {
        color: (label && label.color) || '#333',
        fontSize: (label && label.fontSize) != null ? (typeof label.fontSize === 'number' ? label.fontSize + 'px' : label.fontSize) : '14px',
        fontWeight: (label && label.fontWeight) || 'normal',
        lineHeight: (label && label.lineHeight) || '1.2',
        display: 'flex',
        alignItems: (label && label.alignItems) || 'center',
        justifyContent: (label && label.justifyContent) || 'center',
        backgroundColor: (label && label.bgColor) || 'transparent',
        pointerEvents: selectable ? 'auto' : 'none',
        userSelect: selectable ? 'text' : 'none',
        cursor: selectable ? 'text' : 'default'
      }
      return style
    },
    fieldStyle(field) {
      const style = {}
      if (!field) return style
      if (field.bgColor) style.backgroundColor = field.bgColor
      if (field.borderColor) style.border = `1px solid ${field.borderColor}`
      return style
    },
    fieldInputStyle(field) {
      const style = {}
      if (!field) return style
      if (field.fontSize) style.fontSize = typeof field.fontSize === 'number' ? field.fontSize + 'px' : field.fontSize
      // autoFocus 类型的“功能输入框”，默认隐藏光标；如需显示可显式设置 hideCaret: false
      if (field.autoFocus && field.hideCaret !== false) {
        style.caretColor = 'transparent'
      }
      return style
    },
    fieldLabelStyle(field) {
      const style = {}
      if (!field) return style
      if (field.color) style.color = field.color
      if (field.fontSize) style.fontSize = typeof field.fontSize === 'number' ? field.fontSize + 'px' : field.fontSize
      if (field.fontWeight) style.fontWeight = field.fontWeight
      if (field.textAlign) style.textAlign = field.textAlign
      return style
    },
    focusAutoField() {
      const fields = this.resolvedFields
      if (!Array.isArray(fields) || !fields.length) return
      const target = fields.find(f => f && f.autoFocus)
      if (!target || !target.id) return
      const root = this.$el
      const el = root.querySelector(`.field-input[data-field-id="${target.id}"]`)
      if (el && typeof el.focus === 'function') {
        el.focus()
      }
    },
    focusFieldById(fieldId) {
      if (!fieldId) return
      this.$nextTick(() => {
        const root = this.$el
        const el = root && root.querySelector ? root.querySelector(`.field-input[data-field-id="${fieldId}"]`) : null
        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },
    fieldStyle(field) {
      const style = {}
      if (!field) return style
      if (field.bgColor) style.background = field.bgColor
      if (field.borderColor) style.border = `1px solid ${field.borderColor}`
      return style
    },
    normalizeSrc(val) {
      // 兼容 webpack/vite 的 require/import 返回形式
      if (!val) return val
      if (typeof val === 'string') return val
      if (typeof val === 'object' && typeof val.default === 'string') return val.default
      return val
    },
    reset(stepId = this.initialStepId) {
      if (this._afterEnterTimer) {
        clearTimeout(this._afterEnterTimer)
        this._afterEnterTimer = null
      }
      this.currentStepId = stepId
      this.hoveredHotspotId = ''
      this.ui = { ...this.initialUi }
      this.form = { ...this.initialForm }
      // reset 到同一步骤时，watcher 不会触发；这里补发一次
      this.$emit('step-change', this.currentStepId, this.currentStep, this.resolvedTip)
    },
    rectStyle(rect) {
      const x = (rect.x || 0) * 100
      const y = (rect.y || 0) * 100
      const w = (rect.w || 0) * 100
      const h = (rect.h || 0) * 100
      return {
        left: x + '%',
        top: y + '%',
        width: w + '%',
        height: h + '%',
        cursor: rect.cursor || 'pointer'
      }
    },
    hotspotStyle(hotspot) {
      if (!hotspot) return {}
      const style = { ...(hotspot.style || {}) }
      const highlight = hotspot.highlight
      const isHovered = this.hoveredHotspotId === hotspot.id
      if (!isHovered) return style
      if (highlight === 'primary') {
        style.background = style.background || 'rgba(64, 158, 255, .18)'
        style.border = style.border || '2px solid #409eff'
        style.boxShadow = style.boxShadow || '0 0 10px rgba(64, 158, 255, .7)'
      } else if (highlight === 'warning') {
        style.background = style.background || 'rgba(230, 162, 60, .18)'
        style.border = style.border || '2px solid #e6a23c'
        style.boxShadow = style.boxShadow || '0 0 10px rgba(230, 162, 60, .7)'
      } else if (highlight === 'danger') {
        style.background = style.background || 'rgba(245, 108, 108, .18)'
        style.border = style.border || '2px solid #f56c6c'
        style.boxShadow = style.boxShadow || '0 0 10px rgba(245, 108, 108, .7)'
      } else if (highlight === 'success') {
        style.background = style.background || 'rgba(103, 194, 58, .18)'
        style.border = style.border || '2px solid #67c23a'
        style.boxShadow = style.boxShadow || '0 0 10px rgba(103, 194, 58, .7)'
      }
      if (hotspot.hoverStyle) Object.assign(style, hotspot.hoverStyle)
      return style
    },
    applySet(setter) {
      if (!setter) return
      Object.keys(setter).forEach(k => {
        this.$set(this.ui, k, setter[k])
      })
    },
    applyFormSet(setter) {
      if (!setter) return
      Object.keys(setter).forEach(k => {
        this.$set(this.form, k, setter[k])
      })
    },
    onTableSelectionChange(table, event) {
      if (!table || !event) return
      const rowKey = table.rowKey || 'id'
      const selectedKey = event.row && event.row[rowKey]
      const isRadio = table.selectionType === 'radio'
      let currentKeys = Array.isArray(this.ui[table.id + 'SelectedKeys']) ? this.ui[table.id + 'SelectedKeys'].slice() : (Array.isArray(table.selectedKeys) ? table.selectedKeys.slice() : [])
      let currentRows = Array.isArray(this.ui[table.id + 'SelectedRows']) ? this.ui[table.id + 'SelectedRows'].slice() : []
      const existsIndex = currentKeys.indexOf(selectedKey)

      if (event.checked && isRadio) {
        // 单选：只选中当前行
        currentKeys = [selectedKey]
        currentRows = [{ ...event.row }]
      } else if (event.checked) {
        if (existsIndex < 0) {
          currentKeys.push(selectedKey)
          currentRows.push({ ...event.row })
        }
      } else if (existsIndex > -1) {
        currentKeys.splice(existsIndex, 1)
        currentRows.splice(existsIndex, 1)
      }

      this.$set(this.ui, table.id + 'SelectedKeys', currentKeys)
      this.$set(this.ui, table.id + 'SelectedRows', currentRows)

      if (typeof table.onSelectionChange === 'function') {
        table.onSelectionChange({
          row: event.row,
          checked: event.checked,
          selectedKeys: currentKeys,
          selectedRows: currentRows,
          form: this.form,
          ui: this.ui,
          setForm: this.applyFormSet,
          setUi: this.applySet,
          go: this.go
        })
      }

      this.$emit('step-change', this.currentStepId, this.currentStep, this.resolvedTip)
    },
    onTableCellChange(table, event) {
      if (!table || !event || !event.row) return
      const rowKey = table.rowKey || 'id'
      const currentRows = Array.isArray(this.ui[table.id + 'SelectedRows']) ? this.ui[table.id + 'SelectedRows'].slice() : []
      const targetKey = event.row[rowKey]
      const index = currentRows.findIndex(row => row && row[rowKey] === targetKey)
      if (index > -1) {
        const nextRow = { ...currentRows[index], [event.key]: event.value }
        currentRows.splice(index, 1, nextRow)
        this.$set(this.ui, table.id + 'SelectedRows', currentRows)
      }

      if (typeof table.onCellChange === 'function') {
        table.onCellChange({
          row: event.row,
          key: event.key,
          value: event.value,
          selectedRows: currentRows,
          form: this.form,
          ui: this.ui,
          setForm: this.applyFormSet,
          setUi: this.applySet,
          go: this.go
        })
      }

      this.$emit('step-change', this.currentStepId, this.currentStep, this.resolvedTip)
    },
    onTablePageChange(table, page) {
      if (!table) return
      this.$set(this.ui, table.id + 'Page', page)
      this.$emit('step-change', this.currentStepId, this.currentStep, this.resolvedTip)
    },
    onEnter(hotspot) {
      this.hoveredHotspotId = hotspot && hotspot.id ? hotspot.id : ''
      if (hotspot && hotspot.hoverSet) this.applySet(hotspot.hoverSet)
      if (hotspot && hotspot.on === 'mouseenter') {
        this.guardOrNext(hotspot)
      }
    },
    onLeave(hotspot) {
      if (this.hoveredHotspotId === (hotspot && hotspot.id)) this.hoveredHotspotId = ''
      if (hotspot && hotspot.hoverUnset) this.applySet(hotspot.hoverUnset)
    },
    guardOrNext(hotspot) {
      const guard = hotspot && hotspot.guard
      if (typeof guard === 'function') {
        const res = guard({ ui: this.ui, form: this.form })
        if (res && res.ok === false) {
          this.$emit('guard-failed', res)
          return
        }
      }
      if (hotspot && hotspot.set) this.applySet(hotspot.set)
      if (hotspot && hotspot.setForm) this.applyFormSet(hotspot.setForm)
      if (hotspot && typeof hotspot.action === 'function') {
        const result = hotspot.action({ ui: this.ui, form: this.form }) || {}
        if (result.set) this.applySet(result.set)
        if (result.setForm) this.applyFormSet(result.setForm)
        if (result.next) hotspot = { ...hotspot, next: result.next }
      }
      if (hotspot && hotspot.focusFieldId) this.focusFieldById(hotspot.focusFieldId)
      // 一些热点（如“获取验证码”）只修改 ui，不切换步骤，
      // 这里补发一次 step-change，让父组件同步更新右侧提示。
      if (hotspot && (hotspot.set || hotspot.setForm) && !hotspot.next) {
        this.$emit('step-change', this.currentStepId, this.currentStep, this.resolvedTip)
      }
      if (hotspot && hotspot.next) this.go(hotspot.next)
      if (hotspot && hotspot.emit) this.$emit(hotspot.emit, { stepId: this.currentStepId, hotspotId: hotspot.id })
    },
    onClick(hotspot) {
      if (!hotspot) return
      if (hotspot.on && hotspot.on !== 'click') return
      this.guardOrNext(hotspot)
    },
    onContextMenu(hotspot) {
      if (!hotspot) return
      if (hotspot.on && hotspot.on !== 'contextmenu' && hotspot.on !== 'rightclick') return
      this.guardOrNext(hotspot)
    },
    onDblClick(hotspot) {
      if (!hotspot) return
      if (hotspot.on !== 'dblclick') return
      this.guardOrNext(hotspot)
    },
    onFieldInput(field, evt) {
      const val = evt && evt.target ? evt.target.value : ''
      this.$set(this.form, field.model, val)
      if (typeof field.onInput === 'function') {
        field.onInput({ value: val, ui: this.ui, form: this.form, go: this.go, setUi: this.applySet, setForm: this.applyFormSet, nextTick: this.$nextTick.bind(this) })
      }
    },
    onEnterSubmit(field) {
      if (!field || !field.onEnter) return
      const result = field.onEnter({
        ui: this.ui,
        form: this.form,
        go: this.go,
        setUi: this.applySet,
        set: this.applySet,
        setForm: this.applyFormSet
      })
      if (result && result.ok === false) this.$emit('guard-failed', result)
    },
    go(stepId) {
      if (!this.stepMap[stepId]) {
        this.$emit('unknown-step', stepId)
        return
      }
      this.currentStepId = stepId
      if (this.currentStep && this.currentStep.emitOnEnter) {
        this.$emit(this.currentStep.emitOnEnter, { stepId })
      }
      if (this.currentStep && this.currentStep.isEnd) {
        this.$emit('finish', { stepId })
      }
    }
  }
}
</script>

<style scoped>
.step-player {
  display: flex;
  gap: 16px;
}

.stage {
  position: relative;
  width: 100%;
  aspect-ratio: var(--stage-ratio, 1.766);
  height: auto;
  user-select: none;
}
/* 旧浏览器兜底 */
.stage::before {
  content: '';
  float: left;
  padding-top: calc(100% / var(--stage-ratio, 1.766));
}

.stage::after {
  content: '';
  display: block;
  clear: both;
}

.canvas {
  position: absolute;
  inset: 0;
}

.base-image {
  width: 100% !important;
  height: 100%;
  display: block;
  object-fit: contain;
}

.overlay-image {
  position: absolute;
  left: 0;
  top: 0;
  width: 100% !important;
  height: 100%;
  display: block;
  object-fit: contain;
  pointer-events: none;
}

.label {
  position: absolute;
  white-space: nowrap;
}

.extra-image {
  position: absolute;
  display: block;
  object-fit: contain;
  pointer-events: none;
  box-shadow: 0 0px 16px rgba(0, 0, 0, 0.2);
}

.hotspot {
  position: absolute;
  box-sizing: border-box;
  transition: background .18s ease, border-color .18s ease, box-shadow .18s ease, opacity .18s ease;
}

.field {
  position: absolute;
  display: flex;
  align-items: center;
}

.field-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 0 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.field-textarea {
  resize: none;
  padding-top: 6px;
}

.tip {
  width: 260px;
}

.tip-title {
  font-weight: 700;
  margin-bottom: 8px;
}
</style>



// WEBPACK FOOTER //
// ./src/components/simulate/StepPlayer.vue